// Water: the animated ocean plane, plus rivers and lakes as terrain surface layers.
//
//   ocean plane (one quad, 3 world widths, sea level)
//     depth ramp ◄── real bathymetry (relief texture, bilinear between hex centres)
//     waves      ◄── seamless directional swell + drifting noise chop → normal
//     foam       ◄── signed coast distance from the hex's land/water edges (±~0.85 hex)
//     sea ice    ◄── the snapshot month's NSIDC concentration (hexdata relief.z on sea hexes)
//                    → plates, leads, Worley floes with cracks
//     alpha      ◄── shallow and near-shore water shows the sea floor mesh below
//
//   rivers  (surface layer 110): capsules along river edges, width by river_size, gentle meander,
//                               shimmer that drifts both ways (the data has no flow direction)
//   lakes   (surface layer 100): lake hexes painted as still water, soft shores
//   small lakes (layer 105): soft water decals for lakes smaller than a hex (small-lakes.json)

import * as THREE from 'three/webgpu';
import {
  Fn, If, atan, cameraPosition, cameraViewMatrix, color, cos, dot, float, fwidth, log, max, min, mix, mod, normalize, positionWorld, pow,
  reflect, select, smoothstep, vec2, vec3, vec4,
} from 'three/tsl';
import type Node from 'three/src/nodes/core/Node.js';
import { ROWS, ROW_H, WORLD_W, pattersonY } from '../core/grid.ts';
import { FEATURE, TERRAIN } from '../core/schema.ts';
import type { HexData } from './hexdata.ts';
import type { SurfaceCtx, SurfaceLayer } from './surface.ts';
import { edgeSegmentDistance, hexFrag, neighbourCell, wrapXNode, type HexCell } from './tsl/hex.ts';
import { rgb } from './tsl/color.ts';
import { crossNoise, worldCells, worldFbm, worldNoise } from './tsl/noise.ts';
import { SEA_Y } from './terrain/heights.ts';
import { LIGHT_LOOK } from './lighting.ts';
import { lakeShapes, type LakeShape, type SmallLake } from './smalllakes.ts';

type F = Node<'float'>;
type V2 = Node<'vec2'>;
type V3 = Node<'vec3'>;

export interface WaterDeps {
  data: HexData;
  time: F;
  /** Camera height (hex radii): lifts the pixel footprint at world zoom, so a map baked for the globe stays smooth. */
  camHeight?: F;
}

/** Ocean colours by depth (sRGB), stops on a log-depth scale 0..1; stop 0 is the shallow tone. */
const OCEAN_RAMP: ReadonlyArray<readonly [number, string]> = [
  [0.0, '#5cc4cc'],
  [0.2, '#34b3bf'],
  [0.42, '#1d88aa'],
  [0.68, '#165d91'],
  [1.0, '#0d3563'],
];
const DEPTH_SOFT_M = 15;
const DEPTH_MAX_M = 6000;
const DEPTH_WARP = 0.45; // world units the depth sample wanders
const DEPTH_WARP_FREQ = 0.7;

/**
 * Shallow water by latitude: tropical turquoise, temperate teal, cold grey-teal (sRGB). Cold
 * shelf seas (North Sea, Barents, Baltic) must not look Caribbean. Stops are |latitude| degrees,
 * turned into map z below.
 */
const SHALLOW_BY_LAT: ReadonlyArray<readonly [number, string]> = [
  [25, '#3fb8c9'],
  [55, '#3d8a99'],
  [70, '#2f6f80'],
];
const SHALLOW_DEPTH_M = 400; // the latitude tint fades out below this depth
// Mid- and high-latitude shelf shallows (Azov, Baltic, North Sea) are turbid: a green-brown cast,
// and the pale sea floor stays hidden so they never read as ice or salt flats.
const TURBID = '#5a7d67';
const TURBID_LAT = [35, 45] as const;
const TURBID_DEPTH_M = [40, 5] as const;
const TURBID_SHARE = 0.35;

// Shelf seas: the ramp's shallow stops read as neon from afar, so shelf water loses SHELF_MUTE of its
// chroma and then moves SHELF_TEAL_SHARE of the way toward a muted teal (shares at the surface,
// fading out below the depth range).
const SHELF_TEAL = '#5b8f97';
const SHELF_MUTE = 0.35;
const SHELF_TEAL_SHARE = 0.3;
const LUMA_WEIGHTS = [0.2126, 0.7152, 0.0722] as const;
const SHELF_DEPTH_M = [400, 30] as const;

const WATER_COLORS = {
  foam: '#dff3f5',
  ice: '#e0eaf1',
  iceShade: '#c3d6e3',
  iceLead: '#9cc3da',
  iceWater: '#2b5f7c',
  sky: '#bfdcf2',
  sparkle: '#fff6dc',
  river: '#245f86',
  riverLight: '#4b9bb4',
  riverShimmer: '#8fd0de',
  riverBank: '#4d5a2c',
  riverRim: '#cdbf95',
  lake: '#2f8fa6',
  lakeDeep: '#236c8c',
} as const;

const PLANE_MARGIN = 60;
const WATER_ROUGHNESS = 0.12;
const ICE_ROUGHNESS = 0.55;

// Swell: [wavelength (world units), direction (deg), slope amplitude, speed (rad/s)].
const SWELL: ReadonlyArray<readonly [number, number, number, number]> = [
  [3.1, 18, 0.05, 0.7],
  [1.7, 71, 0.04, 1.0],
  [0.9, -38, 0.03, 1.5],
];
const SWELL_WARP = 2.6;
const CHOP_FREQ = [2.2, 5.5] as const;
const CHOP_SLOPE = 0.16;
const CHOP_DRIFT = 0.06;
const FD_STEP = 0.02; // finite-difference step for noise slopes (world units)

// Foam around coasts, in world units of signed coast distance (+ water side). Foam is a close-zoom
// detail: it fades by pixel footprint (world units per pixel) to a faint line in the mid band and
// to nothing at world zoom, where a surf ring would outline every islet like a marker.
const FOAM_SHORE = 0.12;
const FOAM_FULL_PX = [0.05, 0.02] as const;
const FOAM_MID = 0.35;
const FOAM_OFF_PX = [0.3, 0.14] as const;
const FOAM_BANDS = 2.4; // bands per world unit
const FOAM_BAND_SPEED = 0.07;
const FOAM_REACH = 0.8;
const NO_COAST = 9;

// Sea ice floes (cells per world unit) and sparkle.
const ICE_FREQ = 0.95;
const ICE_PLATE_FREQ = 0.32; // big plates of the pack, visible from the mid band
const ICE_LEAD_WIDTH = [0.012, 0.045] as const; // leads between plates (Worley F2 − F1)
const ICE_SMALL_FREQ = 2.6;
const ICE_CRACK_FREQ = 0.35;
const ICE_AGE_FREQ = 0.6;
// World band: the pack is a mosaic of floes (ICE_FLOE_FREQ) with hard edges, one pixel of
// antialiasing and dark leads about LEAD_PX wide; blurring the ice fraction there reads as cloud.
const ICE_LEAD_PX = 1.2;
const ICE_LEAD_SHARE = 0.15;  // share of floes whose rim opens a lead
const ICE_EDGE_PX = 0.75; // antialiasing half-width of a floe edge, in pixels
const ICE_HASH = 7.13;
// World band floes: about ICE_FLOE_FREQ⁻¹ units across (~11 px at the world view), so the ice edge
// reads as floes, not one plate per six pixels. The cell edges wander (ICE_FLOE_WARP units at
// ICE_FLOE_WARP_FREQ) so no straight Voronoi edges show. Thin cover greys toward the water below
// it, and open water inside the pack is lifted toward the lead tone (less contrast, less static).
const ICE_FLOE_FREQ = 0.16;
const ICE_FLOE_WARP = 1.1;
const ICE_FLOE_WARP_FREQ = 0.4;
const ICE_HOLE_LIFT = 0.3;
const ICE_FLOE_TONE = [0.5, 0.3] as const; // tone floor and per-floe spread (0 = shade, 1 = ice)
const ICE_THIN_SHARE = 0.3;
const ICE_THIN_COVER = [0.15, 0.7] as const;
const ICE_RIDGE_FREQ = 1.4;
const SPARKLE_FREQ = 7;
const SPARKLE_EDGE = [0.9, 0.97] as const;
const SPARKLE_LOBE = [0.55, 0.9] as const; // cosine between the mirrored view ray and the sun
const SUN_DIR = vec3(...LIGHT_LOOK.sunDir).normalize();

// Pixel footprint floor at world zoom: about two globe-texture texels, so the fine chop cannot
// bake into stripes. Zero below FOOTPRINT_FROM, where the real pixel is already smaller.
const FOOTPRINT_PER_HEIGHT = 0.0008;
const FOOTPRINT_FROM = [300, 600] as const;

/** 1 while a detail of wavelength `lambda` spans several pixels, 0 once it would alias. */
function detailFade(px: F, lambda: number): F {
  return smoothstep(lambda * 0.3, lambda * 0.1, px);
}

/** Map-z share of the half height (0 equator → 1 map edge) at a latitude. */
function polarShare(latDeg: number): number {
  return pattersonY((latDeg * Math.PI) / 180) / pattersonY((88.6 * Math.PI) / 180);
}

/** Shallow tone at a polar share (0 equator → 1 map edge), from SHALLOW_BY_LAT. */
function shallowTone(polar: F): V3 {
  let c: V3 = rgb(SHALLOW_BY_LAT[0][1]);
  for (let i = 1; i < SHALLOW_BY_LAT.length; i++) {
    const a = polarShare(SHALLOW_BY_LAT[i - 1][0]);
    const b = polarShare(SHALLOW_BY_LAT[i][0]);
    c = mix(c, rgb(SHALLOW_BY_LAT[i][1]), smoothstep(a, b, polar));
  }
  return c;
}

/** Linear depth-ramp colour for a depth in metres; `shallow` replaces the first stop. */
function oceanColor(depthM: F, shallow: V3): V3 {
  const t = log(depthM.div(DEPTH_SOFT_M).add(1)).div(Math.log(DEPTH_MAX_M / DEPTH_SOFT_M + 1)).clamp(0, 1);
  let c: V3 = shallow;
  for (let i = 1; i < OCEAN_RAMP.length; i++) {
    const [a] = OCEAN_RAMP[i - 1];
    const [b, hex] = OCEAN_RAMP[i];
    c = mix(c, color(hex), smoothstep(a, b, t));
  }
  return c;
}

/** True-ish float (1/0): is this terrain class land for the ocean (lakes count as land)? */
function landOf(terrain: F): F {
  return select(terrain.greaterThan(TERRAIN.COAST + 0.5), float(1), float(0));
}

/**
 * Signed distance to the coast within reach of the hex's own edges: positive over water, negative
 * over land, NO_COAST when no neighbour differs.
 */
function coastDistance(data: HexData, cell: HexCell, local: V2, ownLand: F): F {
  let best: F = float(NO_COAST);
  for (let d = 0; d < 6; d++) {
    const nb = data.terrainAt(neighbourCell(cell, d));
    const differs = landOf(nb.terrain).sub(ownLand).abs().greaterThan(0.5);
    best = min(best, select(differs, edgeSegmentDistance(local, d), float(NO_COAST)));
  }
  return select(ownLand.greaterThan(0.5), best.negate(), best);
}

/** Slope (gradient) of the swell: directional sines whose x wavenumbers repeat every WORLD_W. */
function swellSlope(xz: V2, t: F, warp: F, px: F): V2 {
  let gx: F = float(0);
  let gz: F = float(0);
  for (const [lambda, deg, amp, speed] of SWELL) {
    const a = (deg * Math.PI) / 180;
    const k = (2 * Math.PI) / lambda;
    const cycles = Math.max(1, Math.round((k * Math.cos(a) * WORLD_W) / (2 * Math.PI)));
    const kx = (2 * Math.PI * cycles) / WORLD_W;
    const kz = k * Math.sin(a);
    const phase = xz.x.mul(kx).add(xz.y.mul(kz)).sub(t.mul(speed)).add(warp.mul(SWELL_WARP));
    const c = cos(phase).mul(amp).mul(detailFade(px, lambda));
    const len = Math.hypot(kx, kz);
    gx = gx.add(c.mul(kx / len));
    gz = gz.add(c.mul(kz / len));
  }
  return vec2(gx, gz);
}

/** Slope of drifting noise chop, by finite differences (seamless: worldNoise wraps). */
function chopSlope(xz: V2, t: F, px: F): V2 {
  let g: V2 = vec2(0, 0);
  CHOP_FREQ.forEach((freq, i) => {
    const drift = vec2(t.mul(CHOP_DRIFT * (i + 1)), t.mul(-CHOP_DRIFT * 0.6));
    const p = xz.add(drift);
    const h0 = crossNoise(p, freq);
    const hx = crossNoise(p.add(vec2(FD_STEP, 0)), freq);
    const hz = crossNoise(p.add(vec2(0, FD_STEP)), freq);
    const fade = detailFade(px, 1 / freq);
    g = g.add(vec2(hx.sub(h0), hz.sub(h0)).mul((CHOP_SLOPE / FD_STEP / freq) * (1 / (i + 1))).mul(fade));
  });
  return g;
}

export interface Ocean {
  mesh: THREE.Mesh;
  material: THREE.MeshStandardNodeMaterial;
  dispose(): void;
}

export function createOcean(deps: WaterDeps): Ocean {
  const { data, time } = deps;
  const material = new THREE.MeshStandardNodeMaterial({ transparent: true, metalness: 0, roughness: WATER_ROUGHNESS });
  material.depthWrite = true;

  const alphaOut = float(1).toVar('oceanAlpha');
  const normalOut = vec3(0, 1, 0).toVar('oceanNormal');
  const emissiveOut = vec3(0).toVar('oceanEmissive');
  const roughOut = float(WATER_ROUGHNESS).toVar('oceanRough');

  material.colorNode = Fn(() => {
    const world = positionWorld;
    const xz = vec2(wrapXNode(world.x), world.z);
    const pixel = fwidth(world.xz).length();
    const floor = deps.camHeight ? deps.camHeight.mul(FOOTPRINT_PER_HEIGHT).mul(smoothstep(FOOTPRINT_FROM[0], FOOTPRINT_FROM[1], deps.camHeight)) : float(0);
    const px = max(pixel, floor).toVar('oceanPx');
    const hex = hexFrag(world.xz);
    const own = data.terrainAt(hex);
    const ownLand = landOf(own.terrain);
    // Depth, ice and land between hex centres, sampled at a gently warped point so no straight
    // interpolation edges show in the depth ramp.
    const warp2 = vec2(worldNoise(xz, DEPTH_WARP_FREQ), worldNoise(xz.add(vec2(0, 23.9)), DEPTH_WARP_FREQ)).sub(0.5).mul(DEPTH_WARP * 2);
    const relief = data.reliefSmooth(hexFrag(world.xz.add(warp2), 'depthHex'));
    const depthM = max(relief.x.negate(), 0).toVar('oceanDepth');
    const coast = coastDistance(data, hex, hex.local, ownLand).toVar('oceanCoast');

    // Sea ice: Worley floes where the ice fraction is high; small floes scatter at the ice edge.
    const cover = relief.z.mul(float(1).sub(ownLand));
    const big = worldCells(xz, ICE_FREQ);
    const small = worldCells(xz.add(vec2(0, 13.1)), ICE_SMALL_FREQ);
    // World band: each plate is ice where the cover beats its own threshold, so the edge of the pack
    // breaks into plates with hard edges instead of a soft ramp.
    const plates = worldCells(xz.add(vec2(0, 71.9)), ICE_PLATE_FREQ);
    const floeWarp = vec2(worldNoise(xz, ICE_FLOE_WARP_FREQ), worldNoise(xz.add(vec2(0, 23.9)), ICE_FLOE_WARP_FREQ)).sub(0.5).mul(ICE_FLOE_WARP * 2);
    const floes = worldCells(xz.add(vec2(0, 71.9)).add(floeWarp), ICE_FLOE_FREQ);
    const plateCover = cover.mul(1.2).sub(0.12);
    const plateEdge = fwidth(plateCover).mul(ICE_EDGE_PX);
    const floePlate = smoothstep(floes.z.sub(plateEdge), floes.z.add(plateEdge), plateCover);
    const worldBand = detailFade(px, 1 / ICE_FREQ).oneMinus();
    // Open leads: a few cells stay water even in full pack ice.
    const floeBig = smoothstep(big.z.sub(0.04), big.z.add(0.04), cover.mul(1.2).sub(0.12)).mul(smoothstep(0.97, 0.93, big.z));
    const floeSmall = smoothstep(small.z.sub(0.05), small.z.add(0.05), cover.mul(1.4).sub(0.2));
    // Cracks between floes only near the ice edge and in scattered zones; pack ice reads solid.
    const crackZone = smoothstep(0.9, 0.6, cover).max(smoothstep(0.55, 0.75, worldNoise(xz.add(vec2(0, 41.3)), ICE_CRACK_FREQ)));
    const crackBig = mix(float(1), smoothstep(0.015, 0.06, big.y.sub(big.x)), crackZone.mul(detailFade(px, 0.25)));
    const crackSmall = smoothstep(0.02, 0.08, small.y.sub(small.x)).mul(detailFade(px, 0.12)).add(detailFade(px, 0.12).oneMinus());
    const floeSharp = floeBig.mul(crackBig).max(floeSmall.mul(crackSmall));
    const floe = mix(floePlate, floeSharp, detailFade(px, 1 / ICE_FREQ)).toVar('oceanFloe');
    // Floe rims shade only where the pack is broken; elsewhere the tone drifts smoothly (older ice
    // whiter, young ice greyer) and thin pressure ridges wander across it.
    const rim = smoothstep(0.03, 0.3, big.y.sub(big.x));
    const iceCentre = mix(float(1), rim, crackZone).max(detailFade(px, 0.4).oneMinus());
    const iceAge = worldFbm(xz.add(vec2(0, 17.9)), ICE_AGE_FREQ).mul(0.5).add(big.z.mul(crackZone).mul(0.2));
    const ridge = smoothstep(0.025, 0.0, crossNoise(xz.add(vec2(0, 5.3)), ICE_RIDGE_FREQ).sub(0.5).abs()).mul(detailFade(px, 1 / ICE_RIDGE_FREQ));
    let iceTone: V3 = mix(rgb(WATER_COLORS.iceShade), rgb(WATER_COLORS.ice), iceCentre.mul(0.6).add(iceAge.mul(0.7)).sub(ridge.mul(0.45)).clamp(0, 1));

    // The pack as a mosaic of big plates: each plate its own age tone, thin leads between them
    // with blue subsurface light, so the ice reads as sea ice (not a blank or parchment area).
    const plateAge = plates.z.mul(0.5).add(0.25);
    iceTone = mix(iceTone, iceTone.mul(mix(rgb(WATER_COLORS.iceShade), rgb(WATER_COLORS.ice), plateAge)).mul(1.1), 0.5);
    const lead = smoothstep(ICE_LEAD_WIDTH[1], ICE_LEAD_WIDTH[0], plates.y.sub(plates.x)).mul(detailFade(px, 1 / ICE_PLATE_FREQ));
    iceTone = mix(iceTone, rgb(WATER_COLORS.iceLead), lead.mul(0.5));
    // World band: floes of a soft pale tone, no gradients.
    iceTone = mix(iceTone, mix(rgb(WATER_COLORS.iceShade), rgb(WATER_COLORS.ice), floes.z.mul(ICE_FLOE_TONE[1]).add(ICE_FLOE_TONE[0])), worldBand);
    const leadPx = px.mul(ICE_FLOE_FREQ * ICE_LEAD_PX);
    const opensLead = select(floes.z.mul(ICE_HASH).fract().greaterThan(1 - ICE_LEAD_SHARE), float(1), float(0));
    const worldLead = smoothstep(leadPx, leadPx.mul(0.5), floes.y.sub(floes.x)).mul(opensLead).mul(worldBand);

    // Waves: swell plus drifting chop, calmer under ice.
    const warp = worldNoise(xz.add(time.mul(0.02)), 0.3);
    const calm = float(1).sub(smoothstep(0.2, 0.7, cover));
    const g = swellSlope(xz, time, warp, px).add(chopSlope(xz, time, px)).mul(calm);
    const nWorld = normalize(vec3(g.x.negate(), 1, g.y.negate()));
    normalOut.assign(normalize(cameraViewMatrix.mul(vec4(nWorld, 0)).xyz));

    // Depth colour, lifted near shore; wave faces tinted slightly.
    const polar = world.z.sub(ROWS * ROW_H * 0.5).abs().div(ROWS * ROW_H * 0.5);
    const shallow = shallowTone(polar).toVar('oceanShallow');
    let water: V3 = oceanColor(depthM, mix(rgb(OCEAN_RAMP[0][1]), shallow, smoothstep(SHALLOW_DEPTH_M, 0, depthM)));
    const shelf = smoothstep(SHELF_DEPTH_M[0], SHELF_DEPTH_M[1], depthM);
    water = mix(water, vec3(dot(water, vec3(...LUMA_WEIGHTS))), shelf.mul(SHELF_MUTE));
    water = mix(water, rgb(SHELF_TEAL), shelf.mul(SHELF_TEAL_SHARE));
    const nearShore = smoothstep(0.9, 0.0, coast).mul(smoothstep(-0.2, 0.05, coast));
    water = mix(water, shallow, nearShore.mul(0.3));
    const turbid = smoothstep(polarShare(TURBID_LAT[0]), polarShare(TURBID_LAT[1]), polar)
      .mul(smoothstep(TURBID_DEPTH_M[0], TURBID_DEPTH_M[1], depthM)).toVar('oceanTurbid');
    water = mix(water, rgb(TURBID), turbid.mul(TURBID_SHARE));
    water = water.mul(float(1).add(g.x.add(g.y).mul(0.5)).clamp(0.9, 1.1));
    // Leads in the pack: dark cold water lit from below by the ice around it.
    const pack = smoothstep(0.1, 0.5, cover);
    water = mix(water, mix(rgb(WATER_COLORS.iceWater), rgb(WATER_COLORS.iceLead), pack.mul(0.35)), pack.mul(0.6));
    water = mix(water, rgb(WATER_COLORS.iceLead), pack.mul(worldBand).mul(ICE_HOLE_LIFT));

    // Foam: a bright line at the shore plus bands drifting toward it.
    const foamNoise = worldNoise(xz.add(vec2(time.mul(0.05), 0)), 3.2);
    const foamZoom = mix(float(FOAM_MID), float(1), smoothstep(FOAM_FULL_PX[0], FOAM_FULL_PX[1], px)).mul(smoothstep(FOAM_OFF_PX[0], FOAM_OFF_PX[1], px));
    const shore = smoothstep(FOAM_SHORE, 0.0, coast.add(0.03).sub(foamNoise.mul(0.06))).mul(smoothstep(-0.35, -0.08, coast));
    const bandPhase = coast.mul(FOAM_BANDS).add(time.mul(FOAM_BAND_SPEED)).fract();
    const band = smoothstep(0.1, 0.0, bandPhase.sub(0.5).abs().sub(0.03))
      .mul(smoothstep(FOAM_REACH, 0.15, coast))
      .mul(smoothstep(0.03, 0.12, coast))
      .mul(smoothstep(0.35, 0.65, foamNoise))
      .mul(detailFade(px, 1 / FOAM_BANDS));
    const foam = shore.mul(0.9).max(band.mul(0.5)).mul(float(1).sub(floe)).mul(foamZoom).clamp(0, 1).toVar('oceanFoam');

    let albedo: V3 = mix(water, rgb(WATER_COLORS.foam), foam);
    // World band: ice under thin cover shows the water through it.
    const thin = float(1).sub(smoothstep(ICE_THIN_COVER[0], ICE_THIN_COVER[1], cover)).mul(ICE_THIN_SHARE).mul(worldBand);
    albedo = mix(albedo, mix(iceTone, water, thin), floe);
    albedo = mix(albedo, rgb(WATER_COLORS.iceWater), worldLead.mul(floe));

    // Fake sky reflection, and sun sparkles only where a wave mirrors the sun toward the camera
    // (no environment map on a strategy map).
    const view = normalize(cameraPosition.sub(world));
    const fresnel = pow(float(1).sub(max(view.dot(nWorld), 0)), 5);
    const mirror = max(reflect(view.negate(), nWorld).dot(SUN_DIR), 0);
    const glint = worldNoise(xz.add(vec2(time.mul(0.09), time.mul(-0.06))), SPARKLE_FREQ);
    const sparkle = smoothstep(SPARKLE_EDGE[0], SPARKLE_EDGE[1], glint)
      .mul(smoothstep(SPARKLE_LOBE[0], SPARKLE_LOBE[1], mirror))
      .mul(detailFade(px, 1 / SPARKLE_FREQ)).mul(float(1).sub(floe)).mul(float(1).sub(foam));
    // Ice takes a softer sky sheen than open water.
    const sheen = fresnel.mul(mix(float(0.45), float(0.25), floe));
    emissiveOut.assign(rgb(WATER_COLORS.sky).mul(sheen).add(rgb(WATER_COLORS.sparkle).mul(sparkle.mul(0.4))));
    roughOut.assign(mix(float(WATER_ROUGHNESS), float(ICE_ROUGHNESS), floe.max(foam)));

    // Very shallow and near-shore water shows the sea floor; the rest and ice are opaque.
    const clear = smoothstep(35, 2, depthM);
    const shoreClear = smoothstep(0.3, 0.02, coast.abs());
    const alpha = mix(float(0.97), float(0.6), clear.mul(turbid.oneMinus())).sub(shoreClear.mul(0.25)).max(foam).max(floe);

    // Fade out past the polar map edges.
    const beyond = smoothstep(0, PLANE_MARGIN * 0.5, world.z.negate()).max(smoothstep(ROWS * ROW_H, ROWS * ROW_H + PLANE_MARGIN * 0.5, world.z));
    alphaOut.assign(alpha.clamp(0, 1).mul(beyond.oneMinus()));
    return albedo;
  })();
  material.opacityNode = alphaOut;
  material.normalNode = normalOut;
  material.emissiveNode = emissiveOut;
  material.roughnessNode = roughOut;

  const geo = new THREE.PlaneGeometry(WORLD_W * 3, ROWS * ROW_H + PLANE_MARGIN * 2, 1, 1);
  geo.rotateX(-Math.PI / 2);
  const mesh = new THREE.Mesh(geo, material);
  mesh.position.set(WORLD_W / 2, SEA_Y, (ROWS * ROW_H) / 2);
  mesh.name = 'ocean';
  mesh.receiveShadow = true;
  mesh.renderOrder = 1;
  return {
    mesh,
    material,
    dispose() {
      mesh.removeFromParent();
      geo.dispose();
      material.dispose();
    },
  };
}

// ─── Rivers and lakes (surface layers on the terrain) ───────────────────────────────────────────

// Rivers follow the hex-edge data, but not as a zigzag: the channel runs through the midpoints of
// its river edges and bends around each hex corner along the incircle of the hex that owns both
// edges (a 60° arc tangent to the edges at their midpoints). Consecutive bends alternate sides, so
// a river reads as a smooth meander, never as a hexagonal border.
//
//            corner k                  At corner k three edges meet: H's edges k−1 and k, and the
//        A ·──●──· B                   spoke between neighbours A and B. Each pair of river edges
//         ╲  ╱ ╲  ╱                    bends along the incircle of the hex owning both:
//          ╲╱ H ╲╱                       (k−1, k) → H,  (k−1, spoke) → A,  (k, spoke) → B.
//
// Minor rivers fade out as the camera rises; major rivers keep a hairline down to the world view.
const RIVER_HALF = [0.05, 0.085] as const; // half width in world units: river, major river
const RIVER_MIN_PX = 0.8; // a river line never gets thinner than ~1.6 px
const RIVER_SOFT = 0.3; // soft edge, share of the half width
const RIVER_RIM = 0.045; // pale bank highlight beyond the waterline (world units)
const RIVER_WARP = 0.07; // meander: domain warp of the channel (world units)
const RIVER_WARP_FREQ = 1.3;
const MINOR_FADE = [72, 48] as const; // camera heights: minor rivers gone above the first
const RIVER_SHIMMER_FREQ = 7;
const RIVER_SHIMMER_SPEED = 0.05;
const RIVER_SKY = 0.22;
const RIVER_ROUGHNESS = 0.1;
const FLOOD_REACH = [0.18, 0.46] as const; // floodplain strip: full → none (world units from the channel)
const FLOOD_NOISE = 0.12;
const RIVER_COLORS = {
  shallow: '#3aa6c8',
  deep: '#2a8ab0',
  shimmer: '#9fdcea',
  rim: '#e8dfb8',
  flood: '#5e9a3a',
  floodLush: '#7fb04a',
  floodSilt: '#8a8358',
} as const;

const SQRT3 = Math.sqrt(3);
const INRADIUS = SQRT3 / 2;
const COS30 = Math.cos(Math.PI / 6);
const ARC_COS = COS30 - 1e-3; // a point is on an arc's 60° wedge when within 30° of its middle

type P2 = readonly [number, number];

function dir(deg: number): P2 {
  return [Math.cos((deg * Math.PI) / 180), Math.sin((deg * Math.PI) / 180)];
}

function unit(v: P2): P2 {
  const l = Math.hypot(v[0], v[1]);
  return [v[0] / l, v[1] / l];
}

/** Per corner k of a hex (local frame): the corner point, its two edges, and the three arcs. */
interface CornerGeo {
  corner: P2;
  /** H's edges meeting at the corner: k − 1 and k. */
  ea: number;
  eb: number;
  /** Edge-midpoints of ea and eb. */
  ma: P2;
  mb: P2;
  /** Arc centres and wedge directions: in H, in A (across ea), in B (across eb). */
  own: { c: P2; u: P2 };
  a: { c: P2; u: P2 };
  b: { c: P2; u: P2 };
}

const CORNERS: readonly CornerGeo[] = Array.from({ length: 6 }, (_, k) => {
  const corner = dir(60 * k - 30);
  const ea = (k + 5) % 6;
  const eb = k;
  const na = dir(60 * ea);
  const nb = dir(60 * eb);
  const ca: P2 = [na[0] * SQRT3, na[1] * SQRT3];
  const cb: P2 = [nb[0] * SQRT3, nb[1] * SQRT3];
  return {
    corner,
    ea,
    eb,
    ma: [na[0] * INRADIUS, na[1] * INRADIUS],
    mb: [nb[0] * INRADIUS, nb[1] * INRADIUS],
    own: { c: [0, 0], u: corner },
    a: { c: ca, u: unit([corner[0] - ca[0], corner[1] - ca[1]]) },
    b: { c: cb, u: unit([corner[0] - cb[0], corner[1] - cb[1]]) },
  };
});

/** Distance from p to a 60° incircle arc (centre c, radius INRADIUS, wedge around direction u). */
function arcDistance(p: V2, arc: { c: P2; u: P2 }): F {
  const q = p.sub(vec2(arc.c[0], arc.c[1]));
  const len = q.length();
  const inWedge = q.dot(vec2(arc.u[0], arc.u[1])).greaterThan(len.mul(ARC_COS));
  return select(inWedge, len.sub(INRADIUS).abs(), float(9));
}

/** Distance from p to the segment a–b. */
function segmentDistance(p: V2, a: P2, b: P2): F {
  const ab = vec2(b[0] - a[0], b[1] - a[1]);
  const ap = p.sub(vec2(a[0], a[1]));
  const t = ap.dot(ab).div((b[0] - a[0]) ** 2 + (b[1] - a[1]) ** 2).clamp(0, 1);
  return ap.sub(ab.mul(t)).length();
}

function bitOf(mask: F, d: number): F {
  return mod(mask.div(1 << d).floor(), 2);
}

interface RiverMask {
  /** 0 outside → 1 inside the channel. */
  water: F;
  /** Pale rim just outside the waterline. */
  rim: F;
  /** 1 on the channel's centreline → 0 at its edge. */
  core: F;
  /** Floodplain strip along the channel (FEATURE.FLOODPLAINS hexes only). */
  flood: F;
}

function riverMask(ctx: SurfaceCtx): RiverMask {
  const { data, hex } = ctx;
  const own = data.terrainAt(hex);
  const water = float(0).toVar('riverWater');
  const rim = float(0).toVar('riverRim');
  const core = float(0).toVar('riverCore');
  const flood = float(0).toVar('riverFlood');

  If(own.river.greaterThan(0.5), () => {
    // Meander: the channel is drawn through a gently warped point (both sides of an edge agree,
    // the warp depends on world position only).
    const warp = vec2(worldNoise(ctx.xz, RIVER_WARP_FREQ), worldNoise(ctx.xz.add(vec2(0, 37.7)), RIVER_WARP_FREQ)).sub(0.5).mul(2 * RIVER_WARP);
    const p = hex.local.add(warp);
    const nbs = Array.from({ length: 6 }, (_, d) => data.terrainAt(neighbourCell(hex, d)));
    const minorOn = smoothstep(MINOR_FADE[0], MINOR_FADE[1], ctx.camHeight);
    const minPx = ctx.px.mul(RIVER_MIN_PX);

    // Nearest channel, measured in half widths (so minor and major rivers mix correctly).
    const best = float(9).toVar('riverBest');
    const bestWorld = float(9).toVar('riverBestWorld');
    const bestOn = float(0).toVar('riverBestOn');
    for (const g of CORNERS) {
      const bitA = bitOf(own.river, g.ea);
      const bitB = bitOf(own.river, g.eb);
      const spoke = bitOf(nbs[g.ea].river, (g.eb + 2) % 6);
      const major = own.riverSize.max(nbs[g.ea].riverSize).max(nbs[g.eb].riverSize).greaterThan(1.5);
      const half = max(select(major, float(RIVER_HALF[1]), float(RIVER_HALF[0])), minPx);
      const on = select(major, float(1), minorOn);

      const dOwn = select(bitA.mul(bitB).greaterThan(0.5), arcDistance(p, g.own), float(9));
      const dA = select(bitA.mul(spoke).greaterThan(0.5), arcDistance(p, g.a), float(9));
      const dB = select(bitB.mul(spoke).greaterThan(0.5), arcDistance(p, g.b), float(9));
      // A river edge that ends at this corner (a mouth or a source) runs straight to it.
      const alone = spoke.oneMinus();
      const endA = select(bitA.mul(bitB.oneMinus()).mul(alone).greaterThan(0.5), segmentDistance(p, g.ma, g.corner), float(9));
      const endB = select(bitB.mul(bitA.oneMinus()).mul(alone).greaterThan(0.5), segmentDistance(p, g.mb, g.corner), float(9));
      const d = min(min(dOwn, dA), min(dB, min(endA, endB)));
      const q = d.div(half);
      const closer = q.lessThan(best);
      bestOn.assign(select(closer, on, bestOn));
      bestWorld.assign(min(bestWorld, d));
      best.assign(min(best, q));
    }

    const soft = float(1 - RIVER_SOFT);
    water.assign(smoothstep(1, soft, best).mul(bestOn));
    core.assign(float(1).sub(best).clamp(0, 1));
    const rimReach = float(1).add(max(float(RIVER_RIM), ctx.px).div(max(float(RIVER_HALF[0]), minPx)));
    rim.assign(smoothstep(rimReach, 1, best).mul(water.oneMinus()).mul(bestOn));

    If(own.feature.equal(FEATURE.FLOODPLAINS), () => {
      const n = worldNoise(ctx.xz.add(vec2(0, 19.1)), 2.6).sub(0.5).mul(FLOOD_NOISE);
      flood.assign(smoothstep(FLOOD_REACH[1], FLOOD_REACH[0], bestWorld.add(n)));
    });
  });
  return { water, rim, core, flood };
}

export function riverLayer(): SurfaceLayer {
  // One mask per material build (keyed by that build's hex nodes), shared by color and roughness.
  const cache = new WeakMap<object, RiverMask>();
  const get = (ctx: SurfaceCtx): RiverMask => {
    const hit = cache.get(ctx.hex);
    if (hit) {
      return hit;
    }
    const m = riverMask(ctx);
    cache.set(ctx.hex, m);
    return m;
  };
  return {
    name: 'rivers',
    order: 110,
    color(ctx) {
      const m = get(ctx);

      // Floodplain: fertile green strip hugging the channel, with silty old channels.
      const silt = worldNoise(ctx.xz.add(vec2(0, 5.9)), 3.4);
      const lush = mix(rgb(RIVER_COLORS.flood), rgb(RIVER_COLORS.floodLush), worldNoise(ctx.xz, 1.7));
      const floodTone = mix(lush, rgb(RIVER_COLORS.floodSilt), smoothstep(0.7, 0.9, silt).mul(0.4));
      const ground = mix(ctx.color, floodTone.mul(ctx.color.dot(vec3(0.33)).mul(1.2).add(0.6)), m.flood.mul(0.85));

      // Shimmer drifts both ways: the data has no flow direction, so none is implied.
      const drift = vec2(ctx.time.mul(RIVER_SHIMMER_SPEED), ctx.time.mul(RIVER_SHIMMER_SPEED * 0.6));
      const a = worldNoise(ctx.xz.add(drift), RIVER_SHIMMER_FREQ);
      const b = worldNoise(ctx.xz.sub(drift).add(vec2(0, 9.1)), RIVER_SHIMMER_FREQ * 1.3);
      const shimmer = smoothstep(0.6, 0.9, a.mul(0.5).add(b.mul(0.5))).mul(smoothstep(0.05, 0.02, ctx.px));

      let water: V3 = mix(rgb(RIVER_COLORS.shallow), rgb(RIVER_COLORS.deep), smoothstep(0.2, 0.9, m.core));
      water = mix(water, rgb(RIVER_COLORS.shimmer), shimmer.mul(0.3));
      const banked = mix(ground, rgb(RIVER_COLORS.rim), m.rim.mul(0.55));
      return mix(banked, water, m.water);
    },
    emissive(ctx) {
      // Water mirrors the sky at glancing angles (the terrain has no environment map).
      const m = get(ctx);
      const view = normalize(cameraPosition.sub(ctx.world));
      const fresnel = pow(float(1).sub(max(view.y, 0)), 3);
      return rgb(WATER_COLORS.sky).mul(fresnel.mul(RIVER_SKY).add(0.05).mul(m.water));
    },
    roughness(ctx) {
      return mix(ctx.roughness, float(RIVER_ROUGHNESS), get(ctx).water);
    },
  };
}

// Lakes: water inside lake hexes, with rounded corners where the shore turns, a wandering
// waterline and a soft band of reeds and sand instead of a bright rim.
const LAKE_WATERLINE = [0.03, 0.09] as const;
const LAKE_SHORE_BAND = 0.17;
const LAKE_CORNER = 0.42; // rounding radius of convex shore corners
const LAKE_NOISE = 0.1;
const LAKE_ROUGHNESS = 0.1;
const LAKE_COLORS = { reed: '#7f8f45', sand: '#cdbf92' } as const;

// A lake hex with no lake neighbour (Tsimlyansk Reservoir): the hexagon inset from the hex edge and
// rounded by a disc, so it reads as a pond. Rounding the corners of the hexagon itself scoops a
// six-pointed star out of it.
const POND = { inset: 0.18, round: 0.42, noise: 0.3, noiseFreq: 2.4, depth: 0.55 } as const;

// Lakes smaller than a hex (Titicaca, Balkhash, the Finnish lake district): an inland land hex
// whose `land` fraction says part of it is water shows that share as irregular lakes. The share is
// data; where the water lies inside the hex is not, so the shapes come from noise (thresholded at
// the quantile that covers the share), never as a centred disc that would read as a marker.
const INSET_LAND_MAX = 0.94;
const INSET_ICE_MAX = 0.1;
const INSET_FREQ = [1.1, 2.7] as const; // two octaves: rounded shores, no star-shaped spikes
const INSET_SPREAD = 0.15; // standard deviation of the two-octave noise, for share → threshold
const INSET_EDGE = 0.012;
const INSET_REED = 0.05;

// Normal quantile, Abramowitz & Stegun 26.2.23 (|error| < 4.5e-4).
const PROBIT = { c: [2.515517, 0.802853, 0.010328], d: [1.432788, 0.189269, 0.001308] } as const;

/** z with P(Z > z) = p, for p in (0, 1). */
function upperQuantile(p: F): F {
  const tail = min(p, float(1).sub(p)).clamp(1e-4, 0.5);
  const t = tail.log().mul(-2).sqrt();
  const num = t.mul(t.mul(PROBIT.c[2]).add(PROBIT.c[1])).add(PROBIT.c[0]);
  const den = t.mul(t.mul(t.mul(PROBIT.d[2]).add(PROBIT.d[1])).add(PROBIT.d[0])).add(1);
  const z = t.sub(num.div(den));
  return select(p.lessThan(0.5), z, z.negate());
}

interface LakeMask {
  mask: F;
  depth: F;
  shore: F;
}

/** Inland water inside a land hex: (water 0..1, reed band 0..1); 0 on coasts, rivers and ice. */
function insetLake(ctx: SurfaceCtx): { water: F; reed: F } {
  const { data, hex } = ctx;
  const relief = data.reliefAt(hex);
  let excluded: F = float(0);
  for (let d = 0; d < 6; d++) {
    const t = data.terrainAt(neighbourCell(hex, d)).terrain;
    excluded = excluded.max(select(t.lessThan(TERRAIN.LAKE + 0.5), float(1), float(0)));
  }
  // A river hex's missing land is the river itself; on ice sheets the land mask means ice shelf.
  excluded = excluded.max(select(data.terrainAt(hex).river.greaterThan(0.5), float(1), float(0)));
  excluded = excluded.max(select(relief.z.greaterThan(INSET_ICE_MAX), float(1), float(0)));
  const on = select(relief.w.lessThan(INSET_LAND_MAX), float(1), float(0)).mul(excluded.oneMinus());

  // Share between hex centres, so lake districts do not stop at hex outlines.
  const share = float(1).sub(data.reliefSmooth(hex).w).clamp(0, 1);
  const threshold = upperQuantile(share).mul(INSET_SPREAD).add(0.5);
  const n = worldNoise(ctx.xz.add(vec2(0, 29.3)), INSET_FREQ[0]).mul(0.7).add(worldNoise(ctx.xz.add(vec2(0, 53.9)), INSET_FREQ[1]).mul(0.3));
  const water = smoothstep(threshold.sub(INSET_EDGE), threshold.add(INSET_EDGE), n).mul(on);
  const reed = smoothstep(threshold.sub(INSET_REED), threshold, n).mul(water.oneMinus()).mul(on);
  return { water, reed };
}

/** Signed distance inside a pond's outline (negative outside): a hexagon offset by a disc. */
function pondShore(local: V2): F {
  // The hexagon the outline is rounded around, and how far it is shrunk from the hex's own.
  const inner = INRADIUS - POND.inset - POND.round;
  const scale = inner / INRADIUS;
  let far: F = float(-9);
  let out: F = float(9);
  for (let d = 0; d < 6; d++) {
    const a = (Math.PI / 3) * d;
    far = max(far, local.x.mul(Math.cos(a)).add(local.y.mul(Math.sin(a))));
    out = min(out, edgeSegmentDistance(local.div(scale), d).mul(scale));
  }
  const within = float(inner).sub(far);
  return select(within.greaterThan(0), within.add(POND.round), float(POND.round).sub(out));
}

function lakeMask(ctx: SurfaceCtx): LakeMask {
  const { data, hex } = ctx;
  const own = data.terrainAt(hex);
  const mask = float(0).toVar('lakeMask');
  const depth = float(0).toVar('lakeDepth');
  const band = float(0).toVar('lakeShore');
  const n0 = worldNoise(ctx.xz.add(vec2(0, 61.7)), 3.1).sub(0.5).mul(LAKE_NOISE);
  If(own.terrain.greaterThan(TERRAIN.LAKE + 0.5), () => {
    const inset = insetLake(ctx);
    mask.assign(inset.water);
    depth.assign(inset.water.mul(0.3));
    band.assign(inset.reed);
  });
  If(own.terrain.equal(TERRAIN.LAKE), () => {
    const dry = Array.from({ length: 6 }, (_, d) => data.terrainAt(neighbourCell(hex, d)).terrain.notEqual(TERRAIN.LAKE));
    let shore: F = float(9);
    for (let d = 0; d < 6; d++) {
      shore = min(shore, select(dry[d], edgeSegmentDistance(hex.local, d), float(9)));
    }
    // Round the corners where both edges are shore.
    for (const g of CORNERS) {
      const both = dry[g.ea].and(dry[g.eb]);
      const round = hex.local.sub(vec2(g.corner[0], g.corner[1])).length().sub(LAKE_CORNER);
      shore = select(both, min(shore, round), shore);
    }
    const alone = dry.reduce((all, d) => all.and(d));
    const pond = pondShore(hex.local).add(worldNoise(ctx.xz.add(vec2(0, 17.1)), POND.noiseFreq).sub(0.5).mul(POND.noise));
    const inland = select(alone, pond, shore);
    const s = inland.add(n0);
    mask.assign(smoothstep(LAKE_WATERLINE[0], LAKE_WATERLINE[1], s));
    depth.assign(smoothstep(0.1, select(alone, float(POND.depth), float(0.8)), inland));
    band.assign(smoothstep(LAKE_WATERLINE[0] - LAKE_SHORE_BAND, LAKE_WATERLINE[0], s).mul(mask.oneMinus()));
  });
  return { mask, depth, shore: band };
}

export function lakeLayer(): SurfaceLayer {
  const cache = new WeakMap<object, LakeMask>();
  const get = (ctx: SurfaceCtx): LakeMask => {
    const hit = cache.get(ctx.hex);
    if (hit) {
      return hit;
    }
    const m = lakeMask(ctx);
    cache.set(ctx.hex, m);
    return m;
  };
  return {
    name: 'lakes',
    order: 100,
    color(ctx) {
      const l = get(ctx);
      const ripple = worldNoise(ctx.xz.add(vec2(ctx.time.mul(0.04), 0)), 4);
      const water = mix(color(WATER_COLORS.lake), color(WATER_COLORS.lakeDeep), l.depth).mul(ripple.mul(0.12).add(0.94));
      const shoreTone = mix(rgb(LAKE_COLORS.reed), rgb(LAKE_COLORS.sand), worldNoise(ctx.xz, 5.3));
      const ground = mix(ctx.color, shoreTone, l.shore.mul(0.6));
      return mix(ground, water, l.mask);
    },
    roughness(ctx) {
      return mix(ctx.roughness, float(LAKE_ROUGHNESS), get(ctx).mask);
    },
  };
}

// Small-lake decals (small-lakes.json): outline from smalllakes.ts, in km on the ground. The
// shoreline is 1 in "outline units" (distance over the outline's radius in that direction).
const SMALL_LAKE = {
  /** Softest and sharpest shore blend, in outline units (the sharp end is the anti-alias floor). */
  edge: [0.05, 0.5] as const,
  /** Reed and sand band outside the shore, in outline units. */
  shoreBand: 0.35,
  /** Outline units from the shore at which the water reaches its deep tone. */
  deepAt: 0.2,
  wobble: 0.05,
  wobbleFreq: 6,
  /** Wobble fades out between these pixel footprints (world units per pixel). */
  wobbleFadePx: [0.03, 0.08] as const,
  /** Pixels across the lake between fully faded and fully drawn (a lake smaller than a pixel is noise). */
  fadePx: [1, 3] as const,
  /** Anti-alias width in outline units: pixel footprint in km over the outline radius. */
  aaPx: 1.5,
} as const;

interface DecalMask {
  mask: F;
  depth: F;
  shore: F;
}

/** One lake's mask, depth and shore band at the fragment, folded into the running maxima. */
function addDecal(ctx: SurfaceCtx, s: LakeShape, out: DecalMask): void {
  const reach = s.reachUnits * (1 + SMALL_LAKE.shoreBand);

  // The seam wraps: use the shortest way round.
  const dxRaw = ctx.xz.x.sub(s.x);
  const dx = dxRaw.sub(dxRaw.div(WORLD_W).round().mul(WORLD_W));
  const dz = ctx.xz.y.sub(s.z);

  If(dx.mul(dx).add(dz.mul(dz)).lessThan(reach * reach), () => {
    const km = vec2(dx.mul(s.kmPerX), dz.mul(s.kmPerZ));
    const angle = atan(km.y, km.x);
    let rel: F = float(1);
    for (const h of s.harmonics) {
      rel = rel.add(cos(angle.mul(h.k).add(h.phase)).mul(h.amp));
    }

    // A little wobble so no shore is a clean curve; a close-zoom detail.
    const wobble = worldNoise(ctx.xz.add(vec2(0, 83.1)), SMALL_LAKE.wobbleFreq).sub(0.5).mul(SMALL_LAKE.wobble);
    const wobbleOn = smoothstep(SMALL_LAKE.wobbleFadePx[0], SMALL_LAKE.wobbleFadePx[1], ctx.px).oneMinus();
    const edgeKm = rel.mul(s.radiusKm);
    const nd = km.length().div(edgeKm).mul(wobble.mul(wobbleOn).add(1));
    const pxKm = ctx.px.mul(Math.max(s.kmPerX, s.kmPerZ));
    const soft = pxKm.mul(SMALL_LAKE.aaPx).div(edgeKm).clamp(SMALL_LAKE.edge[0], SMALL_LAKE.edge[1]);

    // A lake far smaller than a pixel fades out instead of smearing into a blob.
    const across = float(s.reachUnits).div(ctx.px);
    const seen = smoothstep(SMALL_LAKE.fadePx[0], SMALL_LAKE.fadePx[1], across);
    const water = smoothstep(soft.oneMinus(), soft.add(1), nd).oneMinus().mul(seen);
    const band = smoothstep(1, 1 + SMALL_LAKE.shoreBand, nd).oneMinus().mul(water.oneMinus()).mul(seen);
    const deep = smoothstep(SMALL_LAKE.deepAt, 1, nd).oneMinus().mul(water);

    out.mask.assign(max(out.mask, water));
    out.depth.assign(max(out.depth, deep));
    out.shore.assign(max(out.shore, band));
  });
}

/** Lakes smaller than a hex as water decals on the terrain, above the lake hexes, below rivers. */
export function smallLakeLayer(lakes: readonly SmallLake[]): SurfaceLayer {
  const shapes = lakeShapes(lakes);
  const cache = new WeakMap<object, DecalMask>();
  const get = (ctx: SurfaceCtx): DecalMask => {
    const hit = cache.get(ctx.hex);
    if (hit) {
      return hit;
    }
    const m: DecalMask = { mask: float(0).toVar('decalMask'), depth: float(0).toVar('decalDepth'), shore: float(0).toVar('decalShore') };
    for (const s of shapes) {
      addDecal(ctx, s, m);
    }
    cache.set(ctx.hex, m);
    return m;
  };
  return {
    name: 'small-lakes',
    order: 105,
    color(ctx) {
      if (shapes.length === 0) {
        return ctx.color;
      }
      const l = get(ctx);
      const ripple = worldNoise(ctx.xz.add(vec2(ctx.time.mul(0.04), 0)), 4);
      const water = mix(color(WATER_COLORS.lake), color(WATER_COLORS.lakeDeep), l.depth).mul(ripple.mul(0.12).add(0.94));
      const shoreTone = mix(rgb(LAKE_COLORS.reed), rgb(LAKE_COLORS.sand), worldNoise(ctx.xz, 5.3));
      const ground = mix(ctx.color, shoreTone, l.shore.mul(0.6));
      return mix(ground, water, l.mask);
    },
    roughness(ctx) {
      if (shapes.length === 0) {
        return ctx.roughness;
      }
      return mix(ctx.roughness, float(LAKE_ROUGHNESS), get(ctx).mask);
    },
  };
}
